import _ from "lodash";
import { dates, flashMessage, modal, router, stm, tr } from "@7willows/sw-lib";
import { match, P } from "ts-pattern";
import type { ChartValue, Param } from "nexus/node/contracts";
import type { Device, LatestMetrics } from "nexus/node/MetricsAccess";
import timestampToDate from "timestamp-to-date";

type LoadData = any;

type InstallationDetails = {
  installationId: string;
  platform: string;
  machineId: string;
};

interface ChartParameters {
  installationId: string;
  params: string[];
  marker: string;
  timeFrom: number;
  timeTo: number;
}

export type FibChart = { dateFrom: number; dateTo: number };
export type FibSeries = { param: string; paramLabel: string; values: ChartValue[] };
export type ChartFor = { marker: string; param: string };
export type Chart = {
  chartId: string;
  chartFor: ChartFor;
  param: Param;
  fibChart: FibChart;
  fibSeries: FibSeries[];
  errMsg: string;
};

const offlineAfterTime = 1000 * 60 * 5;
const lang = tr.getLang();
const installationManager = grow.plant("InstallationManager");
const metricsManager = grow.plant("MetricsManager");

type Msg =
  | [type: "Attr", name: string, value: unknown]
  | [type: "SelectDevice", marker: string]
  | [type: "None"]
  | [type: "SearchPhrase", phrase: string]
  | [type: "RefreshData"]
  | [type: "CreateChart", value: { marker: string; param: Param }]
  | [type: "RemoveChart", chartFor: ChartFor]
  | [type: "LoadChartDataSuccess", chart: Chart]
  | [type: "ChartFromChange", value: { from: string; errMsg: string; chartFor: ChartFor }]
  | [type: "ChartToChange", value: { to: string; errMsg: string; chartFor: ChartFor }]
  | [
    type: "Zoom",
    value: { chartFor: ChartFor; targetDate: { targetDateFrom: number; targetDateTo: number } },
  ]
  | [type: "RemoveSeries", value: { chartFor: ChartFor; param: string }]
  | [type: "AddSeries", chartFor: ChartFor]
  | [
    type: "ChartPredefined",
    data: { chartSelected: Chart; range: "hour" | "day" | "week" | "month" | "year" },
  ]
  | [type: "ChartRangeMove", data: { changedChart: Chart; direction: "left" | "right" }]
  | [type: "DownloadChart", chart: Chart]
  | [type: "DownloadChartFailed"]
  | [type: "DownloadChartSuccess"]
  | [type: "LoadDeviceDetails", data: any] //exchange to LatestMetrics
  | [type: "LoadDataFailed", errMsg: string]
  | [type: "LoadDataSuccess", { data: LatestMetrics; params: Param[] }];

function msg(...args: Msg): Msg {
  return args;
}

interface State {
  isLoading: boolean;
  loadedData?: LatestMetrics;
  paramsDescription: Param[];
  installation?: InstallationDetails;
  selectedDeviceMarker: string;
  lastMetricsCreatedAt: number;
  isDeviceRefreshing: boolean;
  charts: Chart[];
  isChartDataLoading: { [chartId: string]: boolean };
  searchPhrase: string;
}

stm.component({
  tagName: "fib-installation-parameters",
  shadow: false,
  debug: false,
  propTypes: {
    installation: Object,
  },
  attributeChangeFactory: (name, value) => msg("Attr", name, value),
  init(): [State, stm.Cmd<Msg>] {
    return [
      {
        isLoading: false,
        paramsDescription: [],
        selectedDeviceMarker: "",
        lastMetricsCreatedAt: 0,
        isDeviceRefreshing: false,
        charts: [],
        isChartDataLoading: {},
        searchPhrase: "",
      },
      null,
    ];
  },
  update,
  view,
});

function update(state: State, incomingMsg: Msg) {
  return match<Msg, [State, stm.Cmd<Msg>]>(incomingMsg)
    .with(["Attr", "installation", P.select()], (attr) => {
      if (!attr) {
        return [state, null];
      }
      const installation = attr as InstallationDetails;
      return [
        { ...state, isLoading: true, installation: installation },
        loadData(installation),
      ];
    })
    .with(["Attr", P._, P._], () => [state, null])
    .with(["LoadDataFailed", P.select()], (errMsg) => {
      flashMessage(errMsg, "error");
      state.isLoading = false;
      if (state.isDeviceRefreshing) {
        flashMessage(tr("fibTechPanel.deviceRefreshError"), "error");
        state.isDeviceRefreshing = false;
      }
      return [state, null];
    })
    .with(["LoadDataSuccess", P.select()], (loadedData: LoadData) => {
      state.isLoading = false;
      state.lastMetricsCreatedAt = 0;

      if (!loadedData || loadedData.data.length === 0) {
        flashMessage(tr("fibTechPanel.notConnectedString"), "error");
        state.loadedData = { devices: [] };
        return [state, null];
      }

      state.paramsDescription = loadedData.params;
      let timeOffline = Date.now() - offlineAfterTime;
      const len = loadedData.data.length;

      for (let i = 0; i < len; i++) {
        const updateDate = new Date(loadedData.data[i].updatedAt).valueOf() ?? 0;
        if (updateDate < timeOffline) {
          loadedData.data[i].deviceStatus = "offline";
        }
      }

      state.loadedData = { devices: loadedData.data };
      state.isLoading = false;
      return [state, null];
    })
    .with(["SelectDevice", P.select("deviceMarker")], (device) => {
      state.selectedDeviceMarker = device.deviceMarker === state.selectedDeviceMarker
        ? ""
        : device.deviceMarker;
      return [
        state,
        state.selectedDeviceMarker !== "" && state.installation?.machineId
          ? loadDeviceDetails(state.installation, state.selectedDeviceMarker)
          : null,
      ];
    })
    .with(["LoadDeviceDetails", P.select()], (loadedDetails: any) => {
      state.isLoading = false;

      state.loadedData.devices = state.loadedData.devices.map((dev) => {
        return (loadedDetails && loadedDetails.marker && dev.marker === loadedDetails?.marker)
          ? { ...dev, updatedAt: loadedDetails.updatedAt, ...loadedDetails.detail }
          : dev;
      });

      state.lastMetricsCreatedAt = (loadedDetails && loadedDetails.updatedAt)
        ? new Date(loadedDetails?.updatedAt).valueOf()
        : 0;
      if (state.isDeviceRefreshing) {
        flashMessage(tr("fibTechPanel.deviceRefreshEnd"), "info");
        state.isDeviceRefreshing = false;
      }

      return [state, null];
    })
    .with(["None"], () => [state, null])
    .with(["CreateChart", P.select()], ({ marker, param }) => {
      const chartFor: ChartFor = { marker, param: param.param };
      const paramTitle = param.name[lang] === "not defined"
        ? `[${param.param}] [${param.unit}]`
        : `${param.name[lang]} [${param.unit}] `;

      if (!findChart(chartFor)) {
        const dateFrom = Date.now() - 1000 * 60 * 60 * 24;
        const dateTo = Date.now();
        const chart: Chart = {
          chartId: Date.now().toString(36) + Math.random().toString(),
          param,
          chartFor: {
            marker,
            param: param.param,
          },
          fibChart: {
            dateFrom,
            dateTo,
          },
          fibSeries: [{
            param: param.param,
            paramLabel: paramTitle,
            values: [],
          }],
          errMsg: "",
        };
        state.charts.unshift(chart);
        state.isChartDataLoading[state.charts.length - 1] = true;
        const chartParameters: ChartParameters = {
          installationId: state.installation?.installationId ?? "",
          params: [param.param],
          marker,
          timeFrom: dateFrom,
          timeTo: dateTo,
        };
        return [state, loadChartData(chart, chartParameters, state.paramsDescription)];
      }
      return [state, msg("RemoveChart", chartFor)];
    })
    .with(["RemoveChart", P.select()], (chartFor) => {
      _.remove(
        state.charts,
        (chart) =>
          chart.chartFor.marker === chartFor.marker && chart.chartFor.param === chartFor.param,
      );
      return [state, null];
    })
    .with(["LoadChartDataSuccess", P.select()], (chart) => {
      state.isChartDataLoading[chart.chartId] = false;
      return [state, null];
    })
    .with(["Zoom", P.select()], ({ chartFor, targetDate }) => {
      const chart = findChart(chartFor);
      if (!chart) {
        return [state, null];
      }

      if (targetDate.targetDateFrom === Number.POSITIVE_INFINITY) {
        flashMessage(tr("fib.chart.zoomIsMin"), "info");
        state.isLoading = false;
        return [state, null];
      }

      if (targetDate.targetDateFrom === Number.NEGATIVE_INFINITY) {
        flashMessage(tr("fib.chart.zoomIsMax"), "info");
        state.isLoading = false;
        return [state, null];
      }

      chart.fibChart.dateFrom = targetDate.targetDateFrom;
      chart.fibChart.dateTo = targetDate.targetDateTo;
      state.isChartDataLoading[chart.chartId] = true;
      const chartParameters: ChartParameters = extractChartParameters(chart, chartFor);
      return [state, loadChartData(chart, chartParameters, state.paramsDescription)];
    })
    .with(["RemoveSeries", P.select()], ({ chartFor, param }) => {
      const chart = findChart(chartFor);
      if (!chart) {
        return [state, null];
      }
      const series = chart.fibSeries.filter((series) => series.param !== param);
      chart.fibSeries.length <= 1
        ? state.charts = filterCharts(chartFor)
        : chart.fibSeries = series;
      return [state, null];
    })
    .with(["AddSeries", P.select()], (chartFor) => {
      const devices = state.loadedData?.devices.map((device) => ({
        ...device,
        params: device.params,
      }));
      const chart = findChart(chartFor);
      if (!chart) {
        return [state, null];
      }
      const chartParameters: ChartParameters = extractChartParameters(chart, chartFor);
      return [state, openModal(chart, chartParameters, devices, state.paramsDescription)];
    })
    .with(["ChartFromChange", P.select()], ({ from, errMsg, chartFor }) => {
      const fromAsNumber = Date.parse(from);
      const chart = findChart(chartFor);
      if (!chart) {
        return [state, null];
      }
      if (errMsg) {
        chart.errMsg = errMsg;
        return [state, null];
      }
      if (fromAsNumber > chart.fibChart.dateTo) {
        chart.errMsg = tr("fib.chart.dateError");
      } else if (chart.fibChart.dateTo - fromAsNumber < 1000) {
        chart.errMsg = tr("fib.chart.dateBetweenError");
      } else {
        chart.fibChart.dateFrom = fromAsNumber;
        chart.errMsg = "";
      }

      state.isChartDataLoading[chart.chartId] = true;

      const chartParameters: ChartParameters = extractChartParameters(chart, chartFor);
      return [state, loadChartData(chart, chartParameters, state.paramsDescription)];
    })
    .with(["ChartToChange", P.select()], ({ to, errMsg, chartFor }) => {
      const chart = findChart(chartFor);

      if (!chart) {
        return [state, null];
      }

      if (state.isChartDataLoading[chart.chartId]) {
        flashMessage(tr("general.inProgress"), "info");
        return [state, null];
      }

      state.isChartDataLoading[chart.chartId] = true;

      const toAsNumber = Date.parse(to);

      if (errMsg) {
        chart.errMsg = errMsg;
        return [state, null];
      }
      if (toAsNumber < chart.fibChart.dateFrom) {
        chart.errMsg = tr("fib.chart.dateError");
      } else if (toAsNumber - chart.fibChart.dateFrom < 1000) {
        chart.errMsg = tr("fib.chart.dateBetweenError");
      } else {
        chart.fibChart.dateTo = toAsNumber;
        chart.errMsg = "";
      }

      const chartParameters: ChartParameters = extractChartParameters(chart, chartFor);
      return [state, loadChartData(chart, chartParameters, state.paramsDescription)];
    })
    .with(["ChartPredefined", P.select()], (data) => {
      const { chartSelected, range } = data;

      const chart = findChart(chartSelected.chartFor);

      let newFrom, newTo;

      const currentDate = new Date();
      const cYear = currentDate.getFullYear();
      const cMonth = (currentDate.getMonth() + 1).toString().padStart(2, "0");
      const cDay = currentDate.getDate().toString().padStart(2, "0");
      const cHour = currentDate.getHours().toString().padStart(2, "0");

      if (range === "year") {
        // if we put all year range data are aggregated to one number instead of months
        newFrom = cYear + "-01-01 02:00:00";
        newTo = cYear + "-12-25 22:59:59";
      } else if (range === "month") {
        newFrom = cYear + "-" + cMonth + "-01 00:00:00";
        const lastMonthDay = new Date(cYear, currentDate.getMonth() + 1, 0).getDate();
        newTo = cYear + "-" + cMonth + "-" + lastMonthDay + " 23:59:59";
      } else if (range === "week") {
        // its working if sunday is the beginning of month, need to adjust
        const cWeekStart = currentDate.getDate() - currentDate.getDay();
        const cWeekEnd = cWeekStart + 6;

        newFrom = cYear + "-" + cMonth + "-" + cWeekStart.toString().padStart(2, "0") + " 00:00:00";
        newTo = cYear + "-" + cMonth + "-" + cWeekEnd.toString().padStart(2, "0") + " 23:59:59";
      } else if (range === "day") {
        newFrom = cYear + "-" + cMonth + "-" + cDay + " 00:00:00";
        newTo = cYear + "-" + cMonth + "-" + cDay + " 23:59:59";
      } else if (range === "hour") {
        newFrom = cYear + "-" + cMonth + "-" + cDay + " " + cHour + ":00:00";
        newTo = cYear + "-" + cMonth + "-" + cDay + " " + cHour + ":59:59";
      }

      if (!chart) {
        return [state, null];
      }

      if (state.isChartDataLoading[chart.chartId]) {
        flashMessage(tr("general.inProgress"), "info");
        return [state, null];
      }

      state.isChartDataLoading[chart.chartId] = true;

      chart.fibChart.dateTo = Date.parse(newTo as string);
      chart.fibChart.dateFrom = Date.parse(newFrom as string);
      chart.errMsg = "";
      state.isDeviceRefreshing = true;

      const chartParameters: ChartParameters = extractChartParameters(chart, chart.chartFor);

      return [state, loadChartData(chart, chartParameters, state.paramsDescription)];
    })
    .with(["ChartRangeMove", P.select()], (data) => {
      const { changedChart, direction } = data;
      const duration = changedChart.fibChart.dateTo - changedChart.fibChart.dateFrom + 1000;
      let newFrom, newTo;

      if (direction === "left") {
        newFrom = changedChart.fibChart.dateFrom - duration;
        newTo = changedChart.fibChart.dateTo - duration;
      } else {
        newFrom = changedChart.fibChart.dateFrom + duration;
        newTo = changedChart.fibChart.dateTo + duration;
      }

      const chart = findChart(changedChart.chartFor);

      if (!chart) {
        return [state, null];
      }

      if (state.isChartDataLoading[chart.chartId]) {
        flashMessage(tr("general.inProgress"), "info");
        return [state, null];
      }

      state.isChartDataLoading[chart.chartId] = true;

      chart.fibChart.dateTo = newTo;
      chart.fibChart.dateFrom = newFrom;
      chart.errMsg = "";
      state.isDeviceRefreshing = true;

      const chartParameters: ChartParameters = extractChartParameters(chart, chart.chartFor);

      return [state, loadChartData(chart, chartParameters, state.paramsDescription)];
    })
    .with(["DownloadChart", P.select()], (chart) => {
      return [state, downloadChart(chart)];
    })
    .with(["DownloadChartFailed"], () => {
      flashMessage(tr("fib.tech.loadingError"), "error");
      return [state, null];
    })
    .with(["DownloadChartSuccess"], () => {
      return [state, null];
    })
    .with(["SearchPhrase", P.select()], (searchPhrase) => [
      { ...state, searchPhrase },
      null,
    ])
    .with(["RefreshData"], () => {
      if (!state.selectedDeviceMarker || !state.installation) {
        return [state, null];
      }
      state.isDeviceRefreshing = true;
      flashMessage(tr("fibTechPanel.deviceRefreshStarted"), "info");
      return [state, loadDeviceDetails(state.installation, state.selectedDeviceMarker)];
    })
    .exhaustive();

  function findChart(chartFor: ChartFor) {
    return state.charts.find((chart) =>
      chart.chartFor.marker === chartFor.marker && chart.chartFor.param === chartFor.param
    );
  }

  function filterCharts(chartFor: ChartFor) {
    return state.charts.filter((chart) =>
      chart.chartFor.marker !== chartFor.marker && chart.chartFor.param !== chartFor.param
    );
  }

  function extractChartParameters(chart: Chart, chartFor: ChartFor): ChartParameters {
    return {
      installationId: state.installation?.installationId ?? "",
      params: chart.fibSeries.map((series) => series.param),
      marker: chartFor.marker,
      timeFrom: chart.fibChart.dateFrom,
      timeTo: chart.fibChart.dateTo,
    };
  }
}

function downloadChart(chart: Chart) {
  try {
    let allSeries: string = "";

    chart.fibSeries.map((serie) => {
      const dateFormat = "yyyy-MM-dd HH:mm:ss";
      const serieWithHumanDate = serie.values.map((value) => ({
        timestamp: timestampToDate(new Date(value.timestamp).getTime() as number, dateFormat),
        value: value.value,
      }));
      const data = convertToCSV(serieWithHumanDate);
      const csv = `${serie.param}\r\n${
        serie.paramLabel ?? serie.param
      }\r\ntimestamp;value\r\n${data}`;
      allSeries += csv;
    });

    const blob = new Blob([allSeries], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return msg("DownloadChartSuccess");
  } catch (err) {
    return msg("DownloadChartFailed");
  }
}

function convertToCSV(array: any[]) {
  let str = "";

  for (let i = 0; i < array.length; i++) {
    let line = "";
    for (const index in array[i]) {
      if (line != "") {
        line += ";";
      }
      line += array[i][index];
    }
    str += line + "\r\n";
  }

  return str;
}

async function openModal(
  chart: Chart,
  chartParameters: ChartParameters,
  devices: any,
  params: Param[],
) {
  if (!devices) {
    return msg("LoadDataFailed", tr("fib.tech.noDevices"));
  }

  const devicesExt = { devices, params };

  const result = await modal({
    header: tr("fib.chart.newParameter"),
    large: true,
    body: (close) => (
      <fib-select-parameter devices={devicesExt} closeFn={close}></fib-select-parameter>
    ),
  }) as { param: string };

  if (!result) {
    return msg("LoadDataFailed", tr("fib.chart.addingSeriesFailed"));
  }

  if (chartParameters.params.find((param) => param === result.param) === undefined) {
    chartParameters.params.push(result.param);
    return loadChartData(chart, chartParameters, params);
  }
  return msg("LoadDataFailed", tr("fibAdminInstallation.duplicateParamChoosen"));
}

async function loadData(installation: InstallationDetails): Promise<Msg> {
  try {
    const devicesList = await installationManager.getDevices(
      {
        installationId: installation.installationId,
        machineId: installation.machineId,
      },
    );

    if (!devicesList) {
      return msg("LoadDataFailed", tr("fibAdminInstallation.loadDataFailed"));
    }

    const paramsDescription = await installationManager.readInstallationParams(
      {
        instId: installation.installationId,
        platform: installation.platform,
        onlyVisible: true,
      },
    );

    return msg("LoadDataSuccess", { data: devicesList, params: paramsDescription });
  } catch (error) {
    return msg("LoadDataFailed", tr("fibAdminInstallation.loadDataFailed"));
  }
}

async function loadDeviceDetails(
  installation: InstallationDetails,
  deviceMarker: string,
): Promise<Msg> {
  try {
    const data = await installationManager.getDeviceParams(
      {
        installationId: installation.installationId,
        machineId: installation.machineId,
        marker: deviceMarker,
      },
    );

    return msg("LoadDeviceDetails", data);
  } catch (error) {
    return msg("LoadDataFailed", tr("fibAdminInstallation.loadDataFailed"));
  }
}

function getSelectedDevice(state: State): Device {
  return (state.loadedData?.devices ?? []).find((device) =>
    device.marker === state.selectedDeviceMarker
  ) ?? {
    modelName: "",
    marker: "",
    kind: "",
    deviceStatus: "inactive",
  };
}

async function loadChartData(chart: Chart, chartParameters: ChartParameters, params: Param[]) {
  let res;
  try {
    res = await metricsManager.getMetricsInTime(chartParameters);
  } catch (error) {
    return msg("LoadDataFailed", tr("fibAdminInstallation.loadDataFailed"));
  }

  let chartData: FibSeries[] = res.map((item: any) => ({
    ...item,
    paramLabel: tr(findDescription(params, item.param)),
  }));

  if (!chartData.length) {
    chart.fibSeries = chartParameters.params.map((param) => ({
      param,
      paramLabel: tr(findDescription(params, param)),
      values: [],
    }));
  } else {
    chart.fibSeries = chartData;
  }

  return msg("LoadChartDataSuccess", chart);
}

function view(state: State): stm.View<Msg> {
  if (state.isLoading) {
    return <div class={"loader-big"}></div>;
  }
  const selectedDevice = getSelectedDevice(state);
  const { deviceStatus, kind } = selectedDevice;

  const threatAsOffline = Date.now() - offlineAfterTime;

  const shouldDisplayRedDot = deviceStatus !== "ok" && deviceStatus !== "active" &&
    deviceStatus !== "online" && deviceStatus !== "On-grid (Off-grid mode: running)";

  const shouldBeRed = (state.lastMetricsCreatedAt !== 0 &&
    state.lastMetricsCreatedAt < threatAsOffline) || shouldDisplayRedDot;

  const lastUpdate = selectedDevice.marker !== "" && state.lastMetricsCreatedAt !== 0
    ? `${tr("fibTechPanel.updatedAt")} ${dates.formatDateTime(state.lastMetricsCreatedAt)}`
    : "";

  let parameters: any[] = [];

  type DevParam = { device: Device; params: Param };

  if (selectedDevice.marker !== "") {
    parameters = state.paramsDescription
      .filter((p) =>
        p.deviceKind === selectedDevice.kind && p.deviceMarker === selectedDevice.marker
      )
      .map((param) => {
        const a = param.params;
        return { device: selectedDevice, params: a };
      })
      .sort((a: DevParam, b: DevParam) => {
        if (a.params.chart != b.params.chart) {
          return Number(a.params.chart) - Number(b.params.chart);
        }
        return (a.params.name[lang] ?? "").localeCompare(b.params.name[lang] ?? "");
      });
  }

  return (
    <div class="tech-home-panel">
      <div className="grid hbox install-params-main">
        <ul className="box2 list tech-list-devices">
          <div className="h300">{tr("fibTechPanel.devicesList")}</div>
          {renderDevices(state.loadedData.devices ?? [], selectedDevice)}
        </ul>
        <div className="box4 list tech-list-params">
          <div className={"h100 last-update"} title={tr("fibTechPanel.lastUpdate")}>
            <div className={shouldBeRed ? "red" : ""}>
              {lastUpdate}
            </div>
          </div>
          <div className="hbox">
            {state.selectedDeviceMarker && (
              <>
                <div className="h400">
                  {selectedDevice.modelName}
                </div>
              </>
            )}

            {!state.selectedDeviceMarker && (
              <div className="h400">
                {tr("fibTechPanel.selectDevice")}
              </div>
            )}
            <div className="right hbox tech-param-actions">
              {state.selectedDeviceMarker && (
                <div className="h100 text-button-primary" onClick={msg("RefreshData") as any}>
                  <i className="icon-refresh"></i>
                  {tr("fibTechPanel.refresh")}
                </div>
              )}
            </div>
          </div>
          {state.selectedDeviceMarker && (
            <span className="tech-params input-icon-after ">
              <input
                className="body-large list margins"
                type="text"
                value={state.searchPhrase}
                placeholder={tr("fibTechPanel.searchParam")}
                onInput={(event: any) => msg("SearchPhrase", event.target.value)}
              />
              <i className="icon-search"></i>
            </span>
          )}
          {state.selectedDeviceMarker && parameters &&
            renderParameters(parameters, state.charts, state.searchPhrase)}
        </div>
        <div className="vbox box6 tech-list-current">
          {renderCharts(state)}
        </div>
      </div>
    </div>
  );
}

function renderDevices(devices: any[], selectedDevice: string) {
  if (!devices || (devices && devices.length === 0)) {
    return <></>;
  }

  return (
    <>
      {devices.map((device) => {
        const flagSelected = device.marker === selectedDevice;
        const deviceType = tr(`fib.dash.template.${device.kind}`);
        const deviceName = `${device.modelName}`;
        const deviceOffline = device.deviceStatus === "offline";
        return (
          <li
            class={`well ${flagSelected ? "h300 active" : "body-large"} device-title-box`}
            onClick={msg("SelectDevice", device.marker) as any}
          >
            <div className="device-name-line">
              <span>{deviceName.charAt(0).toUpperCase() + deviceName.slice(1)}</span>
              <span
                class={`${deviceOffline ? "small-red-dot" : ""}`}
              >
              </span>
              {flagSelected && <i className="icon-check-fat margin-left"></i>}
            </div>
            <div className="device-kind-line">[{deviceType}]</div>
          </li>
        );
      })}
    </>
  );
}

function findDescription(params: Param[], searchParam: string): string {
  for (const p of params) {
    const param = p.params.find((param: any) =>
      param.param === searchParam && param.name[lang] !== "not defined"
    ) as any;

    if (param) {
      return `${param.name[lang]} [${param.unit}]`;
    }
  }
  return `[${searchParam}]`;
}

function renderParameters(parameters: Device[], charts: any[], searchPhrase: string) {
  if (!parameters || parameters.length === 0) {
    return <></>;
  }

  const device = parameters[0].device;
  const params = parameters[0].params;

  const deviceProps = (Object.keys(device) ?? []).map((a) => {
    const c = _.find(params, { "param": a });

    if (!c) {
      return { id: a, prop: a, "chart": false };
    }

    return { id: a, prop: c.name[lang] ?? "", chart: c.chart };
  }).sort((a: any, b: any) => {
    if (a.chart !== b.chart) {
      return Number(a.chart) - Number(b.chart);
    }
    return (b.prop ?? "").localeCompare(a.prop ?? "", "pl");
  }).sort((a: any, b: any) => {
    return a.prop.localeCompare(b.prop);
  }).map((prop) => {
    return prop.id ?? "";
  });

  return (
    <>
      {deviceProps.map((prop) => {
        const paramDetails = _.find(params, { "param": prop }) ??
          { "name": { "pl": prop }, "chart": false, "param": prop, "unit": "" };
        const isForChart = paramDetails.chart ?? false;
        const hasChart = charts
          .find((chart) =>
            chart.chartFor.marker === device.marker && chart.chartFor.param === paramDetails.param
          );

        if (!paramDetails.isVisible) {
          return <></>;
        }

        const paramTitle = paramDetails.name[lang] === "not defined"
          ? `[${paramDetails.param}]`
          : paramDetails.name[lang];

        return paramDetails.name[lang].toLowerCase().includes(searchPhrase.toLowerCase())
          ? (
            <>
              <li
                class={`well body-large ${hasChart ? "active" : ""} ${
                  isForChart ? "" : "unselectable"
                }`}
                onClick={() =>
                  isForChart
                    ? msg("CreateChart", { marker: device.marker, param: paramDetails }) as any
                    : msg("None") as any}
              >
                {paramTitle}
                <span
                  title={`${findValue(device ?? {}, paramDetails)} ${paramDetails.unit}`}
                >
                  {findValue(device ?? {}, paramDetails)}
                  {!!paramDetails.unit && ` [${paramDetails.unit}]`}
                  {hasChart ? <i class="icon-check-fat margin-left"></i> : ""}
                </span>
              </li>
            </>
          )
          : <></>;
      })}
    </>
  );
}

function findValue(device: Param, param: any) {
  if (!device) {
    return param.zeroValue.toString();
  }
  if (typeof device[param.param] === "number") {
    return device[param.param].toFixed(2);
  }

  const v = (device[param.param] ?? param.zeroValue).toString();
  if (!param.discreet || !param.discreet[v]) {
    if (v === "[0001]") return tr("general.featureOn");
    if (v === "[0000]") return tr("general.featureOff");
    return v;
  }

  return tr(param.discreet[v]);
}

function renderCharts(state: State) {
  return (
    <>
      {state.charts.map((chart) => {
        const dateFormat = "yyyy-MM-ddTHH:mm:ss";
        const from = timestampToDate(chart.fibChart.dateFrom, dateFormat);
        const to = timestampToDate(chart.fibChart.dateTo, dateFormat);
        const paramTitle = chart.param.name[lang] === "not defined"
          ? `[${chart.param.param}]`
          : chart.param.name[lang];
        return (
          <>
            <div className="tech-list-current-wrap">
              <div className="hbox tech-list-header">
                <div className="h300">{paramTitle}</div>
                <div className="h300">{findValue(state, chart.param)} {chart.param.unit}</div>
                <div className="h300 tech-param-actions">
                  <i
                    className="icon-refresh text-button-primary"
                    onClick={() =>
                      msg("ChartToChange", {
                        to: timestampToDate(Date.now(), dateFormat),
                        errMsg: "",
                        chartFor: chart.chartFor,
                      })}
                  >
                  </i>
                  {dates.formatDateTime(Date.now()).slice(-9, -3)}
                </div>
              </div>
              <div
                className="button-tertiary square-button small-button"
                onClick={() => msg("RemoveChart", chart.chartFor) as any}
              >
                <i className="icon-cross"></i>
              </div>
            </div>
            <div className="container chart-top">
              <div className="chart-row">
                <div className="chart-group">
                  <div className="chart-col">
                    <input
                      type={"datetime-local"}
                      className={"body-medium"}
                      value={from}
                      step="1"
                      onChange={(e: Event) => {
                        return msg("ChartFromChange", {
                          from: (e.target as HTMLInputElement).value,
                          errMsg: (e.target as HTMLInputElement).validationMessage,
                          chartFor: chart.chartFor,
                        });
                      }}
                    />
                  </div>
                  <div className="chart-col">
                    <input
                      type={"datetime-local"}
                      className={"body-medium"}
                      value={to}
                      step="1"
                      onChange={(e: Event) =>
                        msg("ChartToChange", {
                          to: (e.target as HTMLInputElement).value,
                          errMsg: (e.target as HTMLInputElement).validationMessage,
                          chartFor: chart.chartFor,
                        })}
                    />
                  </div>
                </div>
                <div className="chart-group">
                  {
                    <button
                      class={"button button-download"}
                      onClick={() => msg("DownloadChart", chart)}
                    >
                      <i class={"icon-download"} />
                    </button>
                  }
                </div>
              </div>
              {chart.errMsg &&
                (
                  <div className={"chart-row"}>
                    <div className={"fib-chart-error"}>{chart.errMsg}</div>
                  </div>
                )}
              <div className="chart-row chart-ranges">
                <div class={"chart-left"}>
                  <button
                    class={"button chart-button"}
                    onClick={() =>
                      msg("ChartRangeMove", { changedChart: chart, direction: "left" })}
                  >
                    <i class={"arrow arrow-left"} />
                  </button>
                </div>
                <div className="chart-group">
                  <div class={"chart-hour"}>
                    <button
                      class={"button chart-button"}
                      onClick={() =>
                        msg("ChartPredefined", { chartSelected: chart, range: "hour" })}
                    >
                      {tr("fib.chart.hour")}
                    </button>
                  </div>
                  <div class={"chart-today"}>
                    <button
                      class={"button chart-button"}
                      onClick={() => msg("ChartPredefined", { chartSelected: chart, range: "day" })}
                    >
                      {tr("fib.chart.day")}
                    </button>
                  </div>
                  <div class={"chart-month"}>
                    <button
                      class={"button chart-button"}
                      onClick={() =>
                        msg("ChartPredefined", { chartSelected: chart, range: "month" })}
                    >
                      {tr("fib.chart.month")}
                    </button>
                  </div>
                  <div class={"chart-year"}>
                    <button
                      class={"button chart-button"}
                      onClick={() =>
                        msg("ChartPredefined", { chartSelected: chart, range: "year" })}
                    >
                      {tr("fib.chart.year")}
                    </button>
                  </div>
                </div>
                <div class={"chart-right"}>
                  <button
                    class={"button chart-button"}
                    onClick={() =>
                      msg("ChartRangeMove", { changedChart: chart, direction: "right" })}
                  >
                    <i class={"arrow arrow-right"} />
                  </button>
                </div>
              </div>
              {state.isChartDataLoading[chart.chartId]
                ? (
                  <div class={"loader-wrapper"}>
                    <div class={"loader-big"}></div>
                  </div>
                )
                : (
                  <fib-chart
                    date-from={chart.fibChart.dateFrom}
                    date-to={chart.fibChart.dateTo}
                    onzoom={(e: CustomEvent) =>
                      state.isChartDataLoading[chart.chartId]
                        ? null
                        : msg("Zoom", { chartFor: chart.chartFor, targetDate: e.detail })}
                  >
                    <>
                      {chart.fibSeries.map((series) =>
                        series.values && series.param
                          ? (
                            <fib-chart-series
                              param={series.paramLabel ?? series.param}
                              values={JSON.stringify(series.values.map((v) => {
                                return { x: v.timestamp, y: v.value };
                              }))}
                            />
                          )
                          : <></>
                      )}
                    </>
                  </fib-chart>
                )}
              <div className="chart-row">
                <div className={"chart-col"}>
                  <span>{tr("fib.chart.devices")}</span>
                  <div className={"chip-group"}>
                    <>
                      {chart.fibSeries.map((series) => (
                        <div
                          className={"chip body-medium"}
                          onClick={() =>
                            msg("RemoveSeries", {
                              chartFor: chart.chartFor,
                              param: series.param,
                            })}
                        >
                          <span className={"big-blue-dot"}></span>
                          <span>{series.paramLabel ?? series.param}</span>
                        </div>
                      ))}
                    </>
                    <div
                      className={"chip body-medium"}
                      onClick={() => msg("AddSeries", chart.chartFor)}
                    >
                      {tr("fib.chart.addNew")}
                      <i className={"icon-plus-bare"} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })}
    </>
  );
}
